import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lock, Users, Car, ChevronDown, ChevronUp, Building2, Key, Unlock, ShieldCheck, Eye, EyeOff, BarChart3, Fuel, MapPin, Cog, Anchor, Printer, Copy, Search } from 'lucide-react';
import AccessDeniedBlock from '../components/AccessDeniedBlock';
import SecretariaDashboard from '../components/SecretariaDashboard';

// Cores para os textos das secretarias
const SECRETARIA_COLORS = [
  '#1e40af', // Azul escuro
  '#047857', // Verde
  '#7c3aed', // Roxo
  '#dc2626', // Vermelho
  '#0891b2', // Ciano
  '#ea580c', // Laranja
  '#4f46e5', // Indigo
  '#0d9488', // Teal
  '#be185d', // Rosa
  '#1d4ed8', // Azul
];

export default function SecretariasPage() {
  const { canManageCadastros, isSecretaria, isAdmin, desbloquearSecretaria, isSecretariaDesbloqueada, secretariaDesbloqueada, canManageInSecretaria } = useAuth();
  
  const [secretarias, setSecretarias] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [comunidades, setComunidades] = useState([]);
  const [postos, setPostos] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [resgateAbasts, setResgateAbasts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [funcDialogOpen, setFuncDialogOpen] = useState(false);
  const [veicDialogOpen, setVeicDialogOpen] = useState(false);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [comDialogOpen, setComDialogOpen] = useState(false);
  const [abastDialogOpen, setAbastDialogOpen] = useState(false);
  
  const [editingId, setEditingId] = useState(null);
  const [editingFuncId, setEditingFuncId] = useState(null);
  const [editingVeicId, setEditingVeicId] = useState(null);
  const [editingComId, setEditingComId] = useState(null);
  const [editingResgateId, setEditingResgateId] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [activeSecretariaId, setActiveSecretariaId] = useState(null);
  
  // Senha dialog
  const [senhaInput, setSenhaInput] = useState('');
  const [senhaSecretariaId, setSenhaSecretariaId] = useState(null);
  const [senhaSecretariaNome, setSenhaSecretariaNome] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [verificandoSenha, setVerificandoSenha] = useState(false);
  
  const [formData, setFormData] = useState({ numero: '', name: '', phone: '', email: '', info: '' });
  const [funcFormData, setFuncFormData] = useState({ numero: '', name: '', position: '', department: '', secretaria_id: '', admission_date: '', status: 'active', veiculo_id: '' });
  const [veicFormData, setVeicFormData] = useState({ code: '', plate: '', description: '', secretaria_id: '', initial_km: 0, status: 'active', combustivel_tipo_id: '' });
  const [comFormData, setComFormData] = useState({ name: '' });
  const [abastFormData, setAbastFormData] = useState({
    date: '', codigo_abastecimento: '', posto_id: '', funcionario_id: '',
    motor_hp: '', comunidade_id: '',
    items: [{ combustivel_id: '', quantity: '', price_per_liter: '' }]
  });
  const [savingAbast, setSavingAbast] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [secRes, funcRes, veicRes, comRes, postosRes, combRes, abastRes] = await Promise.all([
        api.get('/secretarias'),
        api.get('/funcionarios'),
        api.get('/veiculos'),
        api.get('/comunidades'),
        api.get('/postos'),
        api.get('/combustiveis'),
        api.get('/abastecimentos')
      ]);
      setSecretarias(secRes.data);
      setFuncionarios(funcRes.data);
      setVeiculos(veicRes.data);
      setComunidades(comRes.data);
      setPostos(postosRes.data);
      setCombustiveis(combRes.data);
      // Filter SEMSA-RESGATE abastecimentos (those with comunidade_id)
      const allAbast = abastRes.data || [];
      setResgateAbasts(allAbast.filter(a => a.comunidade_id));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Verifica se o usuario com perfil secretaria pode editar (admin sempre pode)
  const canEdit = () => {
    if (isAdmin()) return true;
    if (isSecretaria()) return false; // Secretaria nao pode editar a lista de secretarias
    return canManageCadastros();
  };

  // Verifica se pode editar funcionarios/veiculos de uma secretaria especifica
  const canEditInSecretaria = (secretariaId) => {
    return canManageInSecretaria(secretariaId);
  };

  // === SECRETARIA HANDLERS ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/secretarias/${editingId}`, formData);
        toast.success('Secretaria atualizada!');
      } else {
        await api.post('/secretarias', formData);
        toast.success('Secretaria cadastrada!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar secretaria');
    }
  };

  const handleEdit = (item, e) => {
    e.stopPropagation();
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir esta secretaria?')) return;
    try {
      await api.delete(`/secretarias/${id}`);
      toast.success('Secretaria excluida!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData({ numero: '', name: '', phone: '', email: '', info: '' });
    setEditingId(null);
  };

  // === FUNCIONARIO HANDLERS ===
  const handleFuncSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...funcFormData, secretaria_id: activeSecretariaId };
      if (editingFuncId) {
        await api.put(`/funcionarios/${editingFuncId}`, dataToSend);
        toast.success('Funcionario atualizado!');
      } else {
        await api.post('/funcionarios', dataToSend);
        toast.success('Funcionario cadastrado!');
      }
      setFuncDialogOpen(false);
      resetFuncForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar funcionario');
    }
  };

  const handleFuncEdit = (item, e) => {
    e.stopPropagation();
    setFuncFormData(item);
    setEditingFuncId(item.id);
    setFuncDialogOpen(true);
  };

  const handleFuncDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir este funcionario?')) return;
    try {
      await api.delete(`/funcionarios/${id}`);
      toast.success('Funcionario excluido!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetFuncForm = () => {
    setFuncFormData({ numero: '', name: '', position: '', department: '', secretaria_id: '', admission_date: '', status: 'active', veiculo_id: '' });
    setEditingFuncId(null);
  };

  // === VEICULO HANDLERS ===
  const handleVeicSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...veicFormData, secretaria_id: activeSecretariaId };
      if (editingVeicId) {
        await api.put(`/veiculos/${editingVeicId}`, dataToSend);
        toast.success('Veiculo atualizado!');
      } else {
        await api.post('/veiculos', dataToSend);
        toast.success('Veiculo cadastrado!');
      }
      setVeicDialogOpen(false);
      resetVeicForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar veiculo');
    }
  };

  const handleVeicEdit = (item, e) => {
    e.stopPropagation();
    setVeicFormData(item);
    setEditingVeicId(item.id);
    setVeicDialogOpen(true);
  };

  const handleVeicDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir este veiculo?')) return;
    try {
      await api.delete(`/veiculos/${id}`);
      toast.success('Veiculo excluido!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetVeicForm = () => {
    setVeicFormData({ code: '', plate: '', description: '', secretaria_id: '', initial_km: 0, status: 'active', combustivel_tipo_id: '' });
    setEditingVeicId(null);
  };

  // === COMUNIDADE HANDLERS ===
  const handleComSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingComId) {
        await api.put(`/comunidades/${editingComId}`, comFormData);
        toast.success('Comunidade atualizada!');
      } else {
        await api.post('/comunidades', comFormData);
        toast.success('Comunidade cadastrada!');
      }
      setComDialogOpen(false);
      resetComForm();
      fetchData();
    } catch { toast.error('Erro ao salvar comunidade'); }
  };

  const handleComEdit = (item, e) => {
    e.stopPropagation();
    setComFormData({ name: item.name });
    setEditingComId(item.id);
    setComDialogOpen(true);
  };

  const handleComDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente excluir esta comunidade?')) return;
    try {
      await api.delete(`/comunidades/${id}`);
      toast.success('Comunidade removida!');
      fetchData();
    } catch { toast.error('Erro ao excluir'); }
  };

  const resetComForm = () => {
    setComFormData({ name: '' });
    setEditingComId(null);
  };

  const openAddCom = (secretariaId, e) => {
    e.stopPropagation();
    setActiveSecretariaId(secretariaId);
    resetComForm();
    setComDialogOpen(true);
  };

  const isResgateSecretaria = (sec) => sec && sec.name && (sec.name.toUpperCase().includes('RESGATE') || sec.name.toUpperCase().includes('RESGT'));

  // === ABASTECIMENTO RESGATE HANDLERS ===
  const resetAbastForm = () => {
    setAbastFormData({
      date: '', codigo_abastecimento: '', posto_id: '', funcionario_id: '',
      motor_hp: '', comunidade_id: '',
      items: [{ combustivel_id: '', quantity: '', price_per_liter: '' }]
    });
    setEditingResgateId(null);
  };

  const addAbastItem = () => {
    setAbastFormData(prev => ({
      ...prev,
      items: [...prev.items, { combustivel_id: '', quantity: '', price_per_liter: '' }]
    }));
  };

  const removeAbastItem = (idx) => {
    if (abastFormData.items.length <= 1) return;
    setAbastFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const updateAbastItem = (idx, field, value) => {
    setAbastFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const handleAbastSubmit = async (e) => {
    e.preventDefault();
    if (!abastFormData.date || !abastFormData.funcionario_id || !abastFormData.posto_id) {
      toast.error('Preencha os campos obrigatorios');
      return;
    }
    if (abastFormData.items.some(it => !it.combustivel_id || !it.quantity || !it.price_per_liter)) {
      toast.error('Preencha todos os campos do combustivel');
      return;
    }
    setSavingAbast(true);
    try {
      const payload = {
        date: abastFormData.date,
        codigo_abastecimento: abastFormData.codigo_abastecimento || '',
        posto_id: abastFormData.posto_id,
        funcionario_id: abastFormData.funcionario_id,
        veiculo_id: null,
        invoice_number: abastFormData.codigo_abastecimento || 'RESGATE',
        motor_hp: abastFormData.motor_hp || '',
        comunidade_id: abastFormData.comunidade_id || null,
        odometer_start: 0,
        odometer_end: 0,
        items: abastFormData.items.map(it => ({
          combustivel_id: it.combustivel_id,
          quantity: parseFloat(it.quantity) || 0,
          price_per_liter: parseFloat(it.price_per_liter) || 0,
          total_value: (parseFloat(it.quantity) || 0) * (parseFloat(it.price_per_liter) || 0),
        }))
      };
      if (editingResgateId) {
        await api.put(`/abastecimentos/${editingResgateId}`, payload);
        toast.success('Abastecimento atualizado com sucesso!');
      } else {
        await api.post('/abastecimentos', payload);
        toast.success('Abastecimento registrado com sucesso!');
      }
      setAbastDialogOpen(false);
      setEditingResgateId(null);
      resetAbastForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar abastecimento');
    } finally {
      setSavingAbast(false);
    }
  };

  const getComunidadeNome = (id) => {
    const com = comunidades.find(c => c.id === id);
    return com ? com.name : '-';
  };

  const getPostoNome = (id) => {
    const p = postos.find(x => x.id === id);
    return p ? p.name : '-';
  };

  const getCombustivelNome = (id) => {
    const c = combustiveis.find(x => x.id === id);
    return c ? c.type : '-';
  };

  const getFuncionarioNome = (id) => {
    const f = funcionarios.find(x => x.id === id);
    return f ? f.name : '-';
  };

  // === HANDLERS DE AÇÃO PARA ABASTECIMENTOS RESGATE ===
  const handleDeleteResgate = async (id) => {
    if (!window.confirm('Deseja excluir este abastecimento?')) return;
    try {
      await api.delete(`/abastecimentos/${id}`);
      toast.success('Abastecimento excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handlePrintResgate = async (id) => {
    try {
      const response = await api.get(`/abastecimentos/${id}/print`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar impressão');
    }
  };

  const handleDuplicateResgate = (ab) => {
    setAbastFormData({
      date: new Date().toISOString().split('T')[0],
      codigo_abastecimento: '',
      funcionario_id: ab.funcionario_id || '',
      motor_hp: ab.motor_hp || '',
      comunidade_id: ab.comunidade_id || '',
      posto_id: ab.posto_id || '',
      items: (ab.items || []).map(it => ({
        combustivel_id: it.combustivel_id || '',
        quantity: it.quantity || '',
        price_per_liter: it.price_per_liter || '',
      }))
    });
    setEditingResgateId(null);
    setAbastDialogOpen(true);
    toast.info('Duplicando abastecimento - preencha o CA');
  };

  const handleEditResgate = (ab) => {
    setAbastFormData({
      date: ab.date || new Date().toISOString().split('T')[0],
      codigo_abastecimento: ab.codigo_abastecimento || '',
      funcionario_id: ab.funcionario_id || '',
      motor_hp: ab.motor_hp || '',
      comunidade_id: ab.comunidade_id || '',
      posto_id: ab.posto_id || '',
      items: (ab.items || []).map(it => ({
        combustivel_id: it.combustivel_id || '',
        quantity: it.quantity || '',
        price_per_liter: it.price_per_liter || '',
      }))
    });
    setEditingResgateId(ab.id);
    setAbastDialogOpen(true);
  };

  // === SENHA HANDLERS (para perfil secretaria) ===
  const abrirDialogSenha = (secretariaId, secretariaNome, e) => {
    if (e) e.stopPropagation();
    setSenhaSecretariaId(secretariaId);
    setSenhaSecretariaNome(secretariaNome);
    setSenhaInput('');
    setShowSenha(false);
    setSenhaDialogOpen(true);
  };

  const handleVerificarSenha = async () => {
    if (!senhaInput.trim()) {
      toast.error('Digite a senha');
      return;
    }
    
    setVerificandoSenha(true);
    try {
      const res = await api.post('/secretarias/validar-senha', {
        secretaria_id: senhaSecretariaId,
        senha: senhaInput
      });
      
      if (res.data.valid) {
        desbloquearSecretaria(senhaSecretariaId, senhaSecretariaNome);
        toast.success(`Acesso liberado: ${senhaSecretariaNome}`);
        setSenhaDialogOpen(false);
        setExpandedId(senhaSecretariaId);
        setActiveSecretariaId(senhaSecretariaId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senha incorreta');
    } finally {
      setVerificandoSenha(false);
    }
  };

  const toggleExpand = (id, secretariaNome) => {
    // Se for perfil secretaria e nao esta desbloqueado, abre dialog de senha
    if (isSecretaria() && !isSecretariaDesbloqueada(id)) {
      abrirDialogSenha(id, secretariaNome);
      return;
    }
    
    setExpandedId(expandedId === id ? null : id);
    setActiveSecretariaId(expandedId === id ? null : id);
  };

  const getFuncionariosBySecretaria = (secretariaId) => {
    return funcionarios.filter(f => f.secretaria_id === secretariaId);
  };

  const getVeiculosBySecretaria = (secretariaId) => {
    return veiculos.filter(v => v.secretaria_id === secretariaId);
  };

  const getSecretariaColor = (index) => {
    return SECRETARIA_COLORS[index % SECRETARIA_COLORS.length];
  };

  const openAddFunc = (secretariaId, e) => {
    e.stopPropagation();
    setActiveSecretariaId(secretariaId);
    resetFuncForm();
    setFuncDialogOpen(true);
  };

  const openAddVeic = (secretariaId, e) => {
    e.stopPropagation();
    setActiveSecretariaId(secretariaId);
    resetVeicForm();
    setVeicDialogOpen(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;

  return (
    <div className="space-y-6" data-testid="secretarias-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Secretarias</h2>
          <p className="text-slate-600">
            {isSecretaria() 
              ? 'Clique em uma secretaria e digite a senha para acessar' 
              : 'Clique em uma secretaria para ver funcionarios e veiculos'}
          </p>
        </div>
        
        {/* Indicador de secretaria desbloqueada (apenas para perfil secretaria) */}
        {isSecretaria() && secretariaDesbloqueada && (
          <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg" data-testid="secretaria-desbloqueada-badge">
            <Unlock size={18} />
            <span className="text-sm font-medium">Acesso: {secretariaDesbloqueada.nome}</span>
          </div>
        )}
        
        {canEdit() ? (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-secretaria-button">
                <Plus className="mr-2" size={20} />Nova Secretaria
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingId ? 'Editar' : 'Nova'} Secretaria</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="secretaria-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Numero *</Label>
                    <Input value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>E-mail</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#003366] hover:bg-[#002244]">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        ) : isSecretaria() ? (
          <div className="flex items-center gap-2 text-purple-600 bg-purple-50 px-4 py-2 rounded-lg">
            <Key size={18} />
            <span className="text-sm font-medium">Acesso por Senha</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
            <Lock size={18} />
            <span className="text-sm font-medium">Modo Visualizacao</span>
          </div>
        )}
      </div>

      {/* Dialog Funcionario */}
      <Dialog open={funcDialogOpen} onOpenChange={setFuncDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingFuncId ? 'Editar' : 'Novo'} Funcionario</DialogTitle></DialogHeader>
          <form onSubmit={handleFuncSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Matricula *</Label>
                <Input value={funcFormData.numero} onChange={(e) => setFuncFormData({ ...funcFormData, numero: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={funcFormData.name} onChange={(e) => setFuncFormData({ ...funcFormData, name: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cargo</Label>
                <Input value={funcFormData.position} onChange={(e) => setFuncFormData({ ...funcFormData, position: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Departamento</Label>
                <Input value={funcFormData.department} onChange={(e) => setFuncFormData({ ...funcFormData, department: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Veiculo Vinculado</Label>
              <select 
                value={funcFormData.veiculo_id || ''} 
                onChange={(e) => setFuncFormData({ ...funcFormData, veiculo_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Nenhum veiculo vinculado</option>
                {getVeiculosBySecretaria(activeSecretariaId).map(v => (
                  <option key={v.id} value={v.id}>{v.plate} - {v.description || 'Sem descricao'}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500">Ao selecionar este funcionario no abastecimento, o veiculo sera preenchido automaticamente</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setFuncDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#002244]">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Veiculo */}
      <Dialog open={veicDialogOpen} onOpenChange={setVeicDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingVeicId ? 'Editar' : 'Novo'} Veiculo</DialogTitle></DialogHeader>
          <form onSubmit={handleVeicSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codigo *</Label>
                <Input value={veicFormData.code} onChange={(e) => setVeicFormData({ ...veicFormData, code: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Placa *</Label>
                <Input value={veicFormData.plate} onChange={(e) => setVeicFormData({ ...veicFormData, plate: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Descricao</Label>
                <Input value={veicFormData.description} onChange={(e) => setVeicFormData({ ...veicFormData, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>KM Inicial</Label>
                <Input type="number" value={veicFormData.initial_km} onChange={(e) => setVeicFormData({ ...veicFormData, initial_km: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Combustivel</Label>
              <select
                value={veicFormData.combustivel_tipo_id || ''}
                onChange={(e) => setVeicFormData({ ...veicFormData, combustivel_tipo_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                data-testid="veic-combustivel-tipo-select"
              >
                <option value="">-- Selecione --</option>
                {combustiveis.map(c => (
                  <option key={c.id} value={c.id}>{c.type}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setVeicDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#002244]">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Senha (para perfil secretaria) */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Key size={24} />
              Acesso Restrito
            </DialogTitle>
            <DialogDescription>
              Digite a senha para acessar <strong>{senhaSecretariaNome}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Senha de Acesso:</Label>
              <div className="relative">
                <Input
                  type={showSenha ? "text" : "password"}
                  value={senhaInput}
                  onChange={(e) => setSenhaInput(e.target.value)}
                  placeholder="Digite a senha..."
                  onKeyDown={(e) => e.key === 'Enter' && handleVerificarSenha()}
                  data-testid="senha-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowSenha(!showSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Dica:</strong> A senha foi fornecida pelo administrador do sistema. 
                Se voce nao possui, solicite ao responsavel.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSenhaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleVerificarSenha}
              disabled={verificandoSenha || !senhaInput.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="verificar-senha-btn"
            >
              {verificandoSenha ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Verificando...
                </>
              ) : (
                <>
                  <Unlock size={16} className="mr-2" />
                  Desbloquear
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Comunidade */}
      <Dialog open={comDialogOpen} onOpenChange={setComDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingComId ? 'Editar' : 'Nova'} Comunidade</DialogTitle></DialogHeader>
          <form onSubmit={handleComSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Comunidade *</Label>
              <Input value={comFormData.name} onChange={(e) => setComFormData({ name: e.target.value })} placeholder="Ex: Comunidade Sao Paulo" required data-testid="comunidade-name-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setComDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="save-comunidade-btn">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Novo Abastecimento SEMSA-RESGATE */}
      <Dialog open={abastDialogOpen} onOpenChange={setAbastDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#003366]">
              <Anchor size={20} /> {editingResgateId ? 'Editar' : 'Novo'} Abastecimento - Resgate Fluvial
            </DialogTitle>
            <DialogDescription>{editingResgateId ? 'Edite os dados do abastecimento' : 'Preencha os dados do abastecimento fluvial'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAbastSubmit} className="space-y-4" data-testid="resgate-abast-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input type="date" value={abastFormData.date} onChange={(e) => setAbastFormData(prev => ({ ...prev, date: e.target.value }))} required data-testid="resgate-date" />
              </div>
              <div className="space-y-2">
                <Label>CA (Codigo de Abastecimento)</Label>
                <Input value={abastFormData.codigo_abastecimento} onChange={(e) => setAbastFormData(prev => ({ ...prev, codigo_abastecimento: e.target.value }))} placeholder="Ex: 900" data-testid="resgate-ca" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Motorista *</Label>
                <select value={abastFormData.funcionario_id} onChange={(e) => setAbastFormData(prev => ({ ...prev, funcionario_id: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required data-testid="resgate-motorista">
                  <option value="">Selecione o motorista</option>
                  {funcionarios.filter(f => {
                    const resSec = secretarias.find(s => s.name && (s.name.toUpperCase().includes('RESGATE') || s.name.toUpperCase().includes('RESGT')));
                    return resSec && f.secretaria_id === resSec.id;
                  }).map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Motor/HP</Label>
                <Input value={abastFormData.motor_hp} onChange={(e) => setAbastFormData(prev => ({ ...prev, motor_hp: e.target.value }))} placeholder="Ex: 40HP Mercury" data-testid="resgate-motor" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Comunidade</Label>
                <select value={abastFormData.comunidade_id} onChange={(e) => setAbastFormData(prev => ({ ...prev, comunidade_id: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="resgate-comunidade">
                  <option value="">Selecione a comunidade</option>
                  {comunidades.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Posto (Fornecedor) *</Label>
                <select value={abastFormData.posto_id} onChange={(e) => setAbastFormData(prev => ({ ...prev, posto_id: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required data-testid="resgate-posto">
                  <option value="">Selecione o posto</option>
                  {postos.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Combustiveis */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Combustiveis</Label>
                <Button type="button" size="sm" variant="outline" onClick={addAbastItem} data-testid="add-fuel-item">
                  <Plus size={14} className="mr-1" /> Adicionar Item
                </Button>
              </div>
              {abastFormData.items.map((item, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500">Item {idx + 1}</span>
                    {abastFormData.items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeAbastItem(idx)} className="text-red-500 h-6 px-2">
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Combustivel *</Label>
                      <select value={item.combustivel_id} onChange={(e) => updateAbastItem(idx, 'combustivel_id', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" required data-testid={`fuel-type-${idx}`}>
                        <option value="">Selecione</option>
                        {combustiveis.map(c => (
                          <option key={c.id} value={c.id}>{c.type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Quantidade (L) *</Label>
                      <Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateAbastItem(idx, 'quantity', e.target.value)} placeholder="0.00" required data-testid={`fuel-qty-${idx}`} className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Valor Unit. (R$) *</Label>
                      <Input type="number" step="0.01" value={item.price_per_liter} onChange={(e) => updateAbastItem(idx, 'price_per_liter', e.target.value)} placeholder="0.00" required data-testid={`fuel-price-${idx}`} className="h-9" />
                    </div>
                  </div>
                  {item.quantity && item.price_per_liter && (
                    <p className="text-xs text-right text-green-700 font-semibold">
                      Subtotal: R$ {((parseFloat(item.quantity) || 0) * (parseFloat(item.price_per_liter) || 0)).toFixed(2)}
                    </p>
                  )}
                </div>
              ))}
              {abastFormData.items.some(it => it.quantity && it.price_per_liter) && (
                <p className="text-sm text-right font-bold text-[#003366]">
                  Total: R$ {abastFormData.items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.price_per_liter) || 0), 0).toFixed(2)}
                </p>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setAbastDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" disabled={savingAbast} data-testid="save-resgate-abast">
                {savingAbast ? 'Salvando...' : 'Registrar Abastecimento'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Busca de secretarias */}
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <Input placeholder="Buscar secretaria por nome..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-secretarias" />
      </div>

      {/* Lista de Secretarias */}
      <div className="space-y-4">
        {secretarias
          .filter(s => !searchQuery || (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'))
          .map((secretaria, index) => {
          const color = getSecretariaColor(index);
          const isExpanded = expandedId === secretaria.id;
          const funcList = getFuncionariosBySecretaria(secretaria.id);
          const veicList = getVeiculosBySecretaria(secretaria.id);
          const isDesbloqueada = isSecretariaDesbloqueada(secretaria.id);
          const canEditThisSecretaria = canEditInSecretaria(secretaria.id);
          
          // Para perfil secretaria, mostra cadeado se nao esta desbloqueada
          const showLock = isSecretaria() && !isDesbloqueada;
          
          return (
            <Card 
              key={secretaria.id} 
              className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-l-4 ${showLock ? 'opacity-80' : ''}`}
              style={{ borderLeftColor: color }}
              onClick={() => toggleExpand(secretaria.id, secretaria.name)}
              data-testid={`secretaria-card-${secretaria.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {showLock ? (
                      <Lock size={24} className="text-gray-400" />
                    ) : isDesbloqueada ? (
                      <ShieldCheck size={24} className="text-green-600" />
                    ) : (
                      <Building2 size={24} style={{ color: color }} />
                    )}
                    <div>
                      <CardTitle className="text-xl font-bold" style={{ color: showLock ? '#6b7280' : color }}>
                        {secretaria.name}
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        No {secretaria.numero} {secretaria.phone && `• ${secretaria.phone}`} {secretaria.email && `• ${secretaria.email}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {!showLock && (
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Users size={16} style={{ color: color }} />
                          {funcList.length}
                        </span>
                        <span className="flex items-center gap-1">
                          <Car size={16} style={{ color: color }} />
                          {veicList.length}
                        </span>
                      </div>
                    )}
                    
                    {showLock && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={(e) => abrirDialogSenha(secretaria.id, secretaria.name, e)}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        data-testid={`unlock-btn-${secretaria.id}`}
                      >
                        <Key size={14} className="mr-1" /> Desbloquear
                      </Button>
                    )}
                    
                    {canEdit() && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={(e) => handleEdit(secretaria, e)}>
                          <Pencil size={16} className="text-gray-500" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={(e) => handleDelete(secretaria.id, e)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    )}
                    
                    {!showLock && (
                      isExpanded ? <ChevronUp size={20} style={{ color: color }} /> : <ChevronDown size={20} style={{ color: color }} />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && !showLock && (
                <CardContent className="pt-4 border-t" onClick={(e) => e.stopPropagation()}>
                  {isResgateSecretaria(secretaria) ? (
                    /* ==== SEMSA-RESGATE: Dashboard + Abastecimentos + Funcionarios + Motor/HP + Comunidades ==== */
                    <Tabs defaultValue="dashboard" className="w-full">
                      <TabsList className="grid w-full grid-cols-5 mb-4">
                        <TabsTrigger value="dashboard" className="flex items-center gap-1 text-xs">
                          <BarChart3 size={14} /> Dashboard
                        </TabsTrigger>
                        <TabsTrigger value="abastecimentos" className="flex items-center gap-1 text-xs" data-testid="tab-resgate-abast">
                          <Anchor size={14} /> Abastecimentos ({resgateAbasts.length})
                        </TabsTrigger>
                        <TabsTrigger value="funcionarios" className="flex items-center gap-1 text-xs">
                          <Users size={14} /> Funcionarios ({funcList.length})
                        </TabsTrigger>
                        <TabsTrigger value="veiculos" className="flex items-center gap-1 text-xs">
                          <Cog size={14} /> Motor/HP ({veicList.length})
                        </TabsTrigger>
                        <TabsTrigger value="comunidades" className="flex items-center gap-1 text-xs">
                          <MapPin size={14} /> Comunidades ({comunidades.length})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="dashboard">
                        <SecretariaDashboard secretariaId={secretaria.id} secretariaNome={secretaria.name} color={color} />
                      </TabsContent>

                      {/* Tab Abastecimentos Resgate */}
                      <TabsContent value="abastecimentos">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold flex items-center gap-2 text-[#003366]">
                            <Anchor size={18} /> Abastecimentos Resgate ({resgateAbasts.length})
                          </h4>
                          {canEditThisSecretaria && (
                            <Button size="sm" onClick={() => { resetAbastForm(); setAbastDialogOpen(true); }} className="bg-[#003366] hover:bg-[#002244] text-xs" data-testid="new-resgate-abast-btn">
                              <Plus size={14} className="mr-1" /> Novo Abastecimento
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {resgateAbasts.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-[#003366] text-white">
                                    <th className="px-2 py-2 text-left rounded-tl-lg">CA</th>
                                    <th className="px-2 py-2 text-left">Data</th>
                                    <th className="px-2 py-2 text-left">Motorista</th>
                                    <th className="px-2 py-2 text-left">Comunidade</th>
                                    <th className="px-2 py-2 text-left">Motor/HP</th>
                                    <th className="px-2 py-2 text-right">Litros</th>
                                    <th className="px-2 py-2 text-right">Valor</th>
                                    <th className="px-2 py-2 text-center rounded-tr-lg">Ações</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resgateAbasts.map((ab, idx) => (
                                    <tr key={ab.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-2 py-2 font-medium">{ab.codigo_abastecimento || '-'}</td>
                                      <td className="px-2 py-2">{ab.date ? new Date(ab.date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                                      <td className="px-2 py-2">{getFuncionarioNome(ab.funcionario_id)}</td>
                                      <td className="px-2 py-2">{getComunidadeNome(ab.comunidade_id)}</td>
                                      <td className="px-2 py-2">{ab.motor_hp || '-'}</td>
                                      <td className="px-2 py-2 text-right">{(ab.total_quantity || 0).toFixed(2)} L</td>
                                      <td className="px-2 py-2 text-right font-semibold text-green-700">R$ {(ab.total_value || 0).toFixed(2)}</td>
                                      <td className="px-2 py-2">
                                        <div className="flex gap-1 justify-center">
                                          <Button size="sm" variant="ghost" onClick={() => handlePrintResgate(ab.id)} className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-50" title="Imprimir" data-testid={`print-resgate-${ab.id}`}>
                                            <Printer size={14} />
                                          </Button>
                                          <Button size="sm" variant="ghost" onClick={() => handleDuplicateResgate(ab)} className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50" title="Duplicar" data-testid={`duplicate-resgate-${ab.id}`}>
                                            <Copy size={14} />
                                          </Button>
                                          <Button size="sm" variant="ghost" onClick={() => handleEditResgate(ab)} className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50" title="Editar" data-testid={`edit-resgate-${ab.id}`}>
                                            <Pencil size={14} />
                                          </Button>
                                          <Button size="sm" variant="ghost" onClick={() => handleDeleteResgate(ab.id)} className="h-7 w-7 p-0 text-red-600 hover:text-red-800 hover:bg-red-50" title="Excluir" data-testid={`delete-resgate-${ab.id}`}>
                                            <Trash2 size={14} />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm italic py-8 text-center">Nenhum abastecimento de resgate registrado</p>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="funcionarios">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold flex items-center gap-2" style={{ color }}>
                            <Users size={18} /> Funcionarios ({funcList.length})
                          </h4>
                          {canEditThisSecretaria && (
                            <Button size="sm" variant="outline" onClick={(e) => openAddFunc(secretaria.id, e)} className="text-xs">
                              <Plus size={14} className="mr-1" /> Adicionar
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {funcList.length > 0 ? funcList.map(func => (
                            <div key={func.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center group">
                              <div>
                                <p className="font-medium text-gray-800">{func.name}</p>
                                <p className="text-sm text-gray-500">{func.position || 'Cargo nao informado'}</p>
                              </div>
                              {canEditThisSecretaria && (
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" onClick={(e) => handleFuncEdit(func, e)}><Pencil size={14} /></Button>
                                  <Button size="sm" variant="ghost" onClick={(e) => handleFuncDelete(func.id, e)}><Trash2 size={14} className="text-red-500" /></Button>
                                </div>
                              )}
                            </div>
                          )) : <p className="text-gray-400 text-sm italic py-4 text-center">Nenhum funcionario</p>}
                        </div>
                      </TabsContent>

                      <TabsContent value="veiculos">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold flex items-center gap-2" style={{ color }}>
                            <Cog size={18} /> Motor/HP ({veicList.length})
                          </h4>
                          {canEditThisSecretaria && (
                            <Button size="sm" variant="outline" onClick={(e) => openAddVeic(secretaria.id, e)} className="text-xs">
                              <Plus size={14} className="mr-1" /> Adicionar Motor
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {veicList.length > 0 ? veicList.map(veic => {
                            const combTipo = combustiveis.find(c => c.id === veic.combustivel_tipo_id);
                            return (
                            <div key={veic.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center group">
                              <div>
                                <p className="font-medium text-gray-800">{veic.plate}</p>
                                <p className="text-sm text-gray-500">{veic.description || 'Sem descricao'}</p>
                                {combTipo && <p className="text-xs text-blue-600 font-medium mt-0.5">Combustivel: {combTipo.type}</p>}
                              </div>
                              {canEditThisSecretaria && (
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" onClick={(e) => handleVeicEdit(veic, e)}><Pencil size={14} /></Button>
                                  <Button size="sm" variant="ghost" onClick={(e) => handleVeicDelete(veic.id, e)}><Trash2 size={14} className="text-red-500" /></Button>
                                </div>
                              )}
                            </div>
                            );
                          }) : <p className="text-gray-400 text-sm italic py-4 text-center">Nenhum motor cadastrado</p>}
                        </div>
                      </TabsContent>

                      <TabsContent value="comunidades">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold flex items-center gap-2" style={{ color }}>
                            <MapPin size={18} /> Comunidades ({comunidades.length})
                          </h4>
                          {canEditThisSecretaria && (
                            <Button size="sm" variant="outline" onClick={(e) => openAddCom(secretaria.id, e)} className="text-xs" data-testid="add-comunidade-btn">
                              <Plus size={14} className="mr-1" /> Adicionar Comunidade
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {comunidades.length > 0 ? comunidades.map(com => (
                            <div key={com.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center group">
                              <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-[#003366]" />
                                <p className="font-medium text-gray-800">{com.name}</p>
                              </div>
                              {canEditThisSecretaria && (
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" onClick={(e) => handleComEdit(com, e)}><Pencil size={14} /></Button>
                                  <Button size="sm" variant="ghost" onClick={(e) => handleComDelete(com.id, e)}><Trash2 size={14} className="text-red-500" /></Button>
                                </div>
                              )}
                            </div>
                          )) : <p className="text-gray-400 text-sm italic py-4 text-center">Nenhuma comunidade cadastrada</p>}
                        </div>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    /* ==== DEMAIS SECRETARIAS: Layout normal ==== */
                    <Tabs defaultValue="dashboard" className="w-full">
                      <TabsList className="grid w-full grid-cols-3 mb-4">
                        <TabsTrigger value="dashboard" className="flex items-center gap-2">
                          <BarChart3 size={14} /> Dashboard
                        </TabsTrigger>
                        <TabsTrigger value="funcionarios" className="flex items-center gap-2">
                          <Users size={14} /> Funcionarios ({funcList.length})
                        </TabsTrigger>
                        <TabsTrigger value="veiculos" className="flex items-center gap-2">
                          <Car size={14} /> Veiculos ({veicList.length})
                        </TabsTrigger>
                      </TabsList>

                    {/* Tab Dashboard */}
                    <TabsContent value="dashboard">
                      <SecretariaDashboard 
                        secretariaId={secretaria.id} 
                        secretariaNome={secretaria.name}
                        color={color}
                      />
                    </TabsContent>

                    {/* Tab Funcionarios */}
                    <TabsContent value="funcionarios">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold flex items-center gap-2" style={{ color: color }}>
                          <Users size={18} />
                          Funcionarios ({funcList.length})
                        </h4>
                        {canEditThisSecretaria && (
                          <Button size="sm" variant="outline" onClick={(e) => openAddFunc(secretaria.id, e)} className="text-xs" data-testid={`add-func-btn-${secretaria.id}`}>
                            <Plus size={14} className="mr-1" /> Adicionar
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {funcList.length > 0 ? funcList.map(func => {
                          const veiculoVinculado = func.veiculo_id ? veiculos.find(v => v.id === func.veiculo_id) : null;
                          return (
                            <div key={func.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center group">
                              <div>
                                <p className="font-medium text-gray-800">{func.name}</p>
                                <p className="text-sm text-gray-500">{func.position || 'Cargo nao informado'}</p>
                                {veiculoVinculado && (
                                  <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                                    <Car size={12} /> {veiculoVinculado.plate}
                                  </p>
                                )}
                              </div>
                              {canEditThisSecretaria && (
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" onClick={(e) => handleFuncEdit(func, e)}>
                                    <Pencil size={14} />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={(e) => handleFuncDelete(func.id, e)}>
                                    <Trash2 size={14} className="text-red-500" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        }) : (
                          <p className="text-gray-400 text-sm italic py-4 text-center">Nenhum funcionario</p>
                        )}
                      </div>
                    </TabsContent>

                    {/* Tab Veiculos */}
                    <TabsContent value="veiculos">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold flex items-center gap-2" style={{ color: color }}>
                          <Car size={18} />
                          Veiculos ({veicList.length})
                        </h4>
                        {canEditThisSecretaria && (
                          <Button size="sm" variant="outline" onClick={(e) => openAddVeic(secretaria.id, e)} className="text-xs" data-testid={`add-veic-btn-${secretaria.id}`}>
                            <Plus size={14} className="mr-1" /> Adicionar
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {veicList.length > 0 ? veicList.map(veic => {
                          const combTipo2 = combustiveis.find(c => c.id === veic.combustivel_tipo_id);
                          return (
                          <div key={veic.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center group">
                            <div>
                              <p className="font-medium text-gray-800">{veic.plate}</p>
                              <p className="text-sm text-gray-500">{veic.description || 'Sem descricao'}</p>
                              {combTipo2 && <p className="text-xs text-blue-600 font-medium mt-0.5">Combustivel: {combTipo2.type}</p>}
                            </div>
                            {canEditThisSecretaria && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="sm" variant="ghost" onClick={(e) => handleVeicEdit(veic, e)}>
                                  <Pencil size={14} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={(e) => handleVeicDelete(veic.id, e)}>
                                  <Trash2 size={14} className="text-red-500" />
                                </Button>
                              </div>
                            )}
                          </div>
                          );
                        }) : (
                          <p className="text-gray-400 text-sm italic py-4 text-center">Nenhum veiculo</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {secretarias.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Building2 size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhuma secretaria cadastrada</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
